// import { ls } from 'utils/my-localstorage'
import { find, omit, propEq } from 'ramda'

import { getLocaleId } from 'utils/getLocaleId'
import {
  CHECKBOX_LIST,
  DATE_INTERVAL,
  filterTypes,
  INTERVAL,
  NUMBER,
  PRICE_INTERVAL,
  RADIO_LIST,
  RHEOSTAT,
  RRP_INTERVAL,
  SCORE,
  SEARCH_USERS,
  SWITCHER,
  MULTISELECT_SWITCHER,
} from 'utils/consts'
import { getFiltersCollection } from 'utils/filtersCollections'
import { useGetMyPagePresetsLazyQuery } from 'api/hooks'

export const useGetPresets = () => {
  const [getPresets, { data: presetsData, loading: loadingPresets }] =
    useGetMyPagePresetsLazyQuery({
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    })

  return {
    getPresets,
    presetsData,
    loadingPresets,
  }
}

const LIST_INPUT_TYPE = 'listInput'
const RANGE_INPUT_TYPE = 'rangeInput'
const BOOLEAN_INPUT_TYPE = 'booleanInput'
const COMPLEX_INPUT_TYPE = 'complexInput'
const DATERANGE_INPUT_TYPE = 'dateRangeInput'

export const getFilterInputsForPresetChange = filter => {
  const getComplexChildrenInputs = inputs =>
    inputs.map(input => {
      switch (input?.__typename) {
        case 'FacetListFilterSelection':
          return {
            [LIST_INPUT_TYPE]: omit(['__typename'], input),
          }
        case 'FacetRangeFilterSelection':
          return {
            [RANGE_INPUT_TYPE]: omit(['__typename'], input),
          }

        default:
          break
      }
    })

  switch (filter?.__typename) {
    case 'FacetFreeListFilterSelection':
    case 'FacetListFilterSelection':
      return {
        [LIST_INPUT_TYPE]: omit(['__typename'], filter),
      }
    case 'FacetDateRangeFilterSelection':
      return {
        [DATERANGE_INPUT_TYPE]: omit(['__typename'], filter),
      }
    case 'FacetRangeFilterSelection':
      return {
        [RANGE_INPUT_TYPE]: omit(['__typename'], filter),
      }
    case 'FacetBooleanFilterSelection':
      return {
        [BOOLEAN_INPUT_TYPE]: omit(['__typename'], filter),
      }
    case 'FacetComplexFilterSelection': {
      if (filter.key === 'grapeAndMonocepage') {
        const grapesList =
          omit(
            ['__typename'],
            find(propEq('key', 'grapeAndMonocepage.grape'))(
              filter?.childrenInputs
            )
          ) || {}
        return {
          [COMPLEX_INPUT_TYPE]: {
            key: filter?.key,
            childrenInputs: [
              ...(Object.keys(grapesList).length > 0
                ? [
                    {
                      [LIST_INPUT_TYPE]: grapesList,
                    },
                  ]
                : []),
              {
                [BOOLEAN_INPUT_TYPE]: omit(
                  ['__typename'],
                  find(propEq('key', 'grapeAndMonocepage.monocepage'))(
                    filter?.childrenInputs
                  )
                ),
              },
            ],
          },
        }
      } else {
        return {
          [COMPLEX_INPUT_TYPE]: {
            key: filter?.key,
            childrenInputs: getComplexChildrenInputs(filter?.childrenInputs),
          },
        }
      }
    }

    default:
      break
  }
  filter
}

export const getConvertedFiltersForLS = (filtersCollection, storeName) => {
  return (filtersCollection ?? []).map(filter => {
    const filterFromCollection = find(propEq('apiFilterName', filter?.key))(
      getFiltersCollection(storeName)
    )
    const getFilterValue = f => {
      switch (filterFromCollection?.type) {
        case filterTypes[CHECKBOX_LIST]:
        case filterTypes[RADIO_LIST]:
        case filterTypes[SCORE]:
        case filterTypes[SEARCH_USERS]:
          return f?.byName

        case filterTypes[DATE_INTERVAL]:
          return { fromDate: f?.fromDate, toDate: f?.toDate }

        case filterTypes[RHEOSTAT]:
        case filterTypes[INTERVAL]:
          return { maxValue: f?.maxValue, minValue: f?.minValue }
        case filterTypes[NUMBER]:
          return f?.minValue
        case filterTypes[SWITCHER]:
          return f?.value
        case filterTypes[RRP_INTERVAL]:
        case filterTypes[PRICE_INTERVAL]: {
          const getCurrency = find(
            propEq('__typename', 'FacetListFilterSelection')
          )(f?.childrenInputs)
          const getInterval = find(
            propEq('__typename', 'FacetRangeFilterSelection')
          )(f?.childrenInputs)
          return {
            currency: getCurrency?.byName,
            interval: {
              minValue: getInterval?.minValue,
              maxValue: getInterval?.maxValue,
            },
          }
        }
        case filterTypes[MULTISELECT_SWITCHER]:
          return {
            switcher: find(propEq('__typename', 'FacetBooleanFilterSelection'))(
              f?.childrenInputs
            )?.value,
            list: find(propEq('__typename', 'FacetListFilterSelection'))(
              f?.childrenInputs
            )?.byName,
          }

        default:
          break
      }
    }
    if (filterFromCollection)
      return {
        [filterFromCollection.filterName]: getFilterValue(filter),
      }
  })
}

export const selectPreset = async ({
  toggleSelect,
  preset,
  // filtersStorageName,
  setPresetAndFiltersActions,
  storeName,
}) => {
  toggleSelect()
  // await ls.setObject(filtersStorageName, {})

  setPresetAndFiltersActions({
    filters: getConvertedFiltersForLS(preset?.filters ?? [], storeName),
    preset,
  })
}

export const getVariablesForPresetChange = ({ page, preset }) => {
  return {
    localeId: getLocaleId(),
    input: {
      id: preset?.id,
      name: preset?.name,
      page,
      entity: preset?.entity,
      localeId: getLocaleId(),
      isFavorite: false,
      filters: (preset?.filters ?? []).map(filter =>
        getFilterInputsForPresetChange(filter)
      ),
    },
  }
}
